import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldExclamationIcon, LockOpenIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { deviceSecurityAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

function DeviceSecurity() {
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeLocks, setActiveLocks] = useState([]);
  const [history, setHistory] = useState([]);

  const canAccess = Boolean(
    user?.role === 'admin' ||
    user?.role === 'manager-admin' ||
    (user?.role === 'user' && (user?.isManager || user?.isTeamLead || user?.hasSubordinates))
  );

  const load = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [pendingResponse, locksResponse, historyResponse] = await Promise.all([
        deviceSecurityAPI.getRequests({ status: 'pending' }),
        deviceSecurityAPI.getLocks(),
        deviceSecurityAPI.getRequests({ status: 'all' })
      ]);

      setPendingRequests(pendingResponse.data.requests || []);
      setActiveLocks(locksResponse.data.locks || []);
      setHistory(historyResponse.data.requests || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load device security data');
    } finally {
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (requestId) => {
    try {
      await deviceSecurityAPI.approveRequest(requestId);
      toast.success('Device unlock request approved');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await deviceSecurityAPI.rejectRequest(requestId);
      toast.success('Device unlock request rejected');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject request');
    }
  };

  const handleManualUnlock = async (lockId) => {
    try {
      await deviceSecurityAPI.manualUnlock(lockId);
      toast.success('Device unlocked');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to unlock device');
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">Access Denied</h1>
          <p className="mt-3 text-sm text-slate-600">Manager or admin access is required for device security reviews.</p>
          <Link to="/user-dashboard" className="mt-4 inline-flex rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
            Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 rounded-3xl bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-800 p-6 text-white shadow-xl md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-200">Security</p>
            <h1 className="mt-2 text-3xl font-bold">Device Security</h1>
            <p className="mt-2 text-sm text-indigo-100">Review phones locked for attendance usage and resolve unlock requests.</p>
          </div>
          <Link
            to={user?.role === 'user' ? '/manager-dashboard' : '/'}
            className="inline-flex items-center rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur"
          >
            Back to Dashboard
          </Link>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white p-6 shadow-sm">Loading device security data...</div>
        ) : (
          <div className="space-y-8">
            <section className="rounded-2xl bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <ShieldExclamationIcon className="h-6 w-6 text-amber-500" />
                <h2 className="text-xl font-semibold text-slate-900">Pending Unlock Requests</h2>
              </div>
              <div className="space-y-3">
                {pendingRequests.length === 0 ? (
                  <p className="text-sm text-slate-500">No pending device unlock requests.</p>
                ) : pendingRequests.map((request) => (
                  <div key={request.id} className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {request.requester?.firstName} {request.requester?.lastName}
                        </p>
                        <p className="text-sm text-slate-600">{request.requesterEmail}</p>
                        <p className="mt-1 text-xs text-slate-500">Locked email: {request.lockedEmail}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprove(request.id)}
                          className="inline-flex items-center rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white"
                        >
                          <LockOpenIcon className="mr-2 h-4 w-4" />
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(request.id)}
                          className="inline-flex items-center rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white"
                        >
                          <XCircleIcon className="mr-2 h-4 w-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <ShieldExclamationIcon className="h-6 w-6 text-indigo-500" />
                <h2 className="text-xl font-semibold text-slate-900">Active Device Locks</h2>
              </div>
              <div className="space-y-3">
                {activeLocks.length === 0 ? (
                  <p className="text-sm text-slate-500">No active device locks.</p>
                ) : activeLocks.map((lock) => (
                  <div key={lock.id} className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {lock.lockedUser?.firstName} {lock.lockedUser?.lastName}
                        </p>
                        <p className="text-sm text-slate-600">{lock.lockedEmail}</p>
                        <p className="mt-1 text-xs text-slate-500">Device ID: {lock.deviceId}</p>
                      </div>
                      {isSuperAdmin() && (
                        <button
                          type="button"
                          onClick={() => handleManualUnlock(lock.id)}
                          className="inline-flex items-center rounded-xl border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-700"
                        >
                          Manual Unlock
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">History</h2>
              <div className="mt-4 space-y-3">
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500">No device unlock history yet.</p>
                ) : history.map((request) => (
                  <div key={request.id} className="rounded-2xl border border-slate-200 p-4">
                    <p className="font-medium text-slate-900">{request.requesterEmail}</p>
                    <p className="text-sm text-slate-600">
                      {request.status} · locked email {request.lockedEmail}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceSecurity;
